import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { IDENTITY_PATHS, sessionStore } from '@hds/identity-client-sdk';

import { resolveValue } from './resolveValue.js';
import { useShellWords } from './shellWords.js';

// The App Menu store — the contract this package exists for.
//
// The menu is the same four account doors in every app (Profile, Sign-in &
// security, Settings, Log Out), plus whatever else each app puts beside them.
// The four are here from the start — an app that places the shell does nothing
// to get them — and an app or a feature registers its own rows beside them, or
// removes one it does not want.
//
// It is a Pinia store rather than an object an app passes around, so anything
// in the app reaches the same menu with `useAppMenuStore()`: a feature adding
// its row at startup, the avatar that opens the drawer, the drawer itself. A
// store is one per application, not one per module, so two apps in one process
// — a test run, a storybook — never share a menu.
//
// A row is a plain object: a route to go to, or an action to run.
//
// Labels may be a string or a function returning one. The function form is what
// keeps a menu correct across a language change: `t('account.menu.profile')`
// read once at registration would freeze whichever locale happened to be active.

const DEFAULT_GROUP = 'app';

// An app's own rows, then the account doors, then the way out.
const DEFAULT_GROUP_ORDER = ['app', 'account', 'session'];

// Where the shell's own two pages live. Sign-in & security is the identity
// package's page, on the identity package's path.
const SHELL_PATHS = Object.freeze({
    profile: '/profile',
    settings: '/settings'
});

function assertUsableEntry(entry, registered) {
    if (!entry || typeof entry !== 'object') {
        throw new Error('APP_MENU_ENTRY_INVALID');
    }
    if (typeof entry.id !== 'string' || entry.id.length === 0) {
        throw new Error('APP_MENU_ENTRY_ID_REQUIRED');
    }
    if (registered.some((existing) => existing.id === entry.id)) {
        // Two features claiming one id would silently shadow each other, and
        // which one won would depend on import order.
        throw new Error('APP_MENU_ENTRY_DUPLICATE');
    }
    if (entry.label === undefined || entry.label === null) {
        throw new Error('APP_MENU_ENTRY_LABEL_REQUIRED');
    }
    const hasRoute = typeof entry.to === 'string' && entry.to.length > 0;
    const hasAction = typeof entry.onSelect === 'function';
    if (hasRoute === hasAction) {
        // Exactly one. Neither means a dead row; both means the shell has to
        // guess which the author meant.
        throw new Error('APP_MENU_ENTRY_NEEDS_ONE_OF_TO_OR_ON_SELECT');
    }
}

const useAppMenuStore = defineStore('appMenu', function () {
    const word = useShellWords();
    const session = sessionStore();

    // The four doors every app carries. They are entries like any other, so an
    // app removes one with `removeItem`, and it can do that at any time because
    // they are here before anything else is. A label is a getter, so it follows
    // a language change.
    const standardDoors = [
        { id: 'profile', label: () => word('shell.menu.profile'), icon: 'user', group: 'account', order: 10, to: SHELL_PATHS.profile },
        { id: 'security', label: () => word('shell.menu.signInSecurity'), icon: 'shield', group: 'account', order: 20, to: IDENTITY_PATHS.signInSecurity },
        { id: 'settings', label: () => word('shell.menu.settings'), icon: 'cog', group: 'account', order: 30, to: SHELL_PATHS.settings },
        { id: 'log-out', label: () => word('shell.menu.logOut'), icon: 'sign-out', tone: 'danger', group: 'session', onSelect: () => session.logout() }
    ];

    // A shallow ref, replaced on every change. An entry carries functions — a
    // label getter, an action — that must reach the menu as they were handed
    // over, and a deep ref would wrap each entry in a proxy.
    const registered = shallowRef([...standardDoors]);
    const groupOrder = ref([...DEFAULT_GROUP_ORDER]);
    const isOpen = ref(false);

    function register(...entries) {
        for (const entry of entries.flat()) {
            assertUsableEntry(entry, registered.value);
            registered.value = [...registered.value, entry];
        }
    }

    // Take a row out — one an app added, or one that arrived by default. An id
    // nobody registered is refused rather than ignored: removing a row before
    // the feature that owns it has added it would otherwise do nothing, and the
    // row would appear anyway with no sign of why.
    function removeItem(id) {
        if (!registered.value.some((entry) => entry.id === id)) {
            throw new Error('APP_MENU_ENTRY_NOT_REGISTERED');
        }
        registered.value = registered.value.filter((entry) => entry.id !== id);
    }

    // Fix the order groups render in. A group nobody declared still renders —
    // after the declared ones, in the order it was first registered — because a
    // missing declaration should cost a feature its position, never its
    // presence.
    function declareGroups(groups) {
        groupOrder.value = [...groups];
    }

    function open() {
        isOpen.value = true;
    }

    function close() {
        isOpen.value = false;
    }

    function groupRank(group) {
        const declared = groupOrder.value.indexOf(group);
        if (declared !== -1) {
            return declared;
        }
        // Undeclared groups sort after every declared one, in first-seen order.
        const firstSeen = registered.value.findIndex((entry) => (entry.group ?? DEFAULT_GROUP) === group);
        return groupOrder.value.length + firstSeen;
    }

    // The shell renders THIS, never the raw registrations: display values are
    // resolved here, so a template never calls a function it was handed.
    const entries = computed(() => registered.value.map((entry) => ({
        id: entry.id,
        label: resolveValue(entry.label),
        sub: resolveValue(entry.sub) ?? null,
        icon: entry.icon ?? null,
        to: entry.to ?? null,
        onSelect: entry.onSelect ?? null,
        group: entry.group ?? DEFAULT_GROUP,
        order: entry.order ?? 0,
        attention: resolveValue(entry.attention) === true,
        tone: entry.tone ?? 'default'
    })));

    // Grouped and sorted, ready to render. Registration order breaks a tie on
    // `order`, so two features that both forgot it stay stable.
    const sections = computed(() => {
        const bucketed = new Map();
        for (const entry of entries.value) {
            if (!bucketed.has(entry.group)) {
                bucketed.set(entry.group, []);
            }
            bucketed.get(entry.group).push(entry);
        }
        return [...bucketed.entries()]
            .sort(([left], [right]) => groupRank(left) - groupRank(right))
            .map(([group, groupEntries]) => ({
                group,
                entries: [...groupEntries].sort((left, right) => left.order - right.order)
            }));
    });

    return { registered, groupOrder, isOpen, entries, sections, register, removeItem, declareGroups, open, close };
});

export { SHELL_PATHS, useAppMenuStore };
